/**
 * API Service
 * Centralized API communication with the backend
 * (session cookie is sent automatically via withCredentials)
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  withCredentials: true,
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// ============================================================================
// Authentication
// ============================================================================

export const signup = async (userData) => {
  const response = await api.post('/auth/signup', userData);
  return response.data;
};

export const login = async (tenant, token) => {
  const response = await api.post('/auth/login', { tenant, token });
  return response.data;
};

export const adminLogin = async (password) => {
  const response = await api.post('/auth/admin-login', { password });
  return response.data;
};

export const logout = async () => {
  const response = await api.post('/auth/logout');
  return response.data;
};

export const getMe = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

// ============================================================================
// User Management
// ============================================================================

export const getUsers = async () => {
  const response = await api.get('/users');
  return response.data;
};

export const getUser = async (id) => {
  const response = await api.get(`/users/${id}`);
  return response.data;
};

export const updateUser = async (id, userData) => {
  const response = await api.put(`/users/${id}`, userData);
  return response.data;
};

export const deleteUser = async (id) => {
  const response = await api.delete(`/users/${id}`);
  return response.data;
};

// ============================================================================
// Household Members
// ============================================================================

export const getMembers = async () => {
  const response = await api.get('/members');
  return response.data;
};

export const createMember = async (memberData) => {
  const response = await api.post('/members', memberData);
  return response.data;
};

export const updateMember = async (id, memberData) => {
  const response = await api.put(`/members/${id}`, memberData);
  return response.data;
};

export const deleteMember = async (id) => {
  const response = await api.delete(`/members/${id}`);
  return response.data;
};

// ============================================================================
// Parking Logs
// ============================================================================

export const getParkingLogs = async (userId = null, days = null) => {
  const params = {};
  if (userId) params.userId = userId;
  if (days) params.days = days;

  const response = await api.get('/parking-logs', { params });
  return response.data;
};

export const registerParkingManually = async (userId, durationHours = null, params = null) => {
  const response = await api.post(`/parking/register/${userId}`, { durationHours, params });
  return response.data;
};

// ============================================================================
// Parking Statistics
// ============================================================================

export const getMonthlyParkingStats = async (month, userId = null) => {
  const params = { month };
  if (userId) params.userId = userId;
  const response = await api.get('/parking-stats/monthly', { params });
  return response.data;
};

export const getAvailableMonths = async (userId = null) => {
  const params = {};
  if (userId) params.userId = userId;
  const response = await api.get('/parking-stats/available-months', { params });
  return response.data;
};

// ============================================================================
// Scheduler Control
// ============================================================================

export const runSchedulerNow = async () => {
  const response = await api.post('/scheduler/run');
  return response.data;
};

export const checkHealth = async () => {
  const response = await api.get('/health');
  return response.data;
};

export default api;
